import { useData } from "../../hooks/useData";
import { Months } from "../../shared/types/Months";
import { ImageContainer } from "../ImageContainer/ImageContainer";
//import BadgeImage from "../../assets/Badge.svg";
import "./CalendarMonth.scss";

type Holiday = {
  day: number
  name: string
  block: boolean
}

type Props = {
  totalDays: number
  monthName: Months
  offset: number
  monthNumber: number
  featured?: boolean
  showYear?: boolean
  holidaysBr?: Array<Holiday>
  holidaysJp?: Array<Holiday>
}

export function CalendarMonth(props: Props) {
  const { orientation, selectedImages } = useData()
  const { totalDays, holidaysJp, monthName, offset, holidaysBr = [] } = props
  const weekDaysJp = ['日', '月', '火', '水', '木', '金', '土']
  const weekDaysBr = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
  const monthTranslations: {[keys in Months]: string } = {
    january: 'Janeiro',
    february: 'Fevereiro',
    march: 'Março',
    april: 'Abril',
    may: 'Maio',
    june: 'Junho',
    july: 'Julho',
    august: 'Agosto',
    september: 'Setembro',
    october: 'Outubro',
    november: 'Novembro',
    december: 'Dezembro'
  }

  const monthNumbers: { [key in Months]: number } = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12
  }

  const japaneseMonthSymbol = '月';

  const currentImage = selectedImages[monthName]
  const author = [
    currentImage?.name || '',
    currentImage?.age ? `${currentImage?.age} anos` : ''
  ]
  const city = [
    currentImage?.city || '',
    currentImage?.uf || ''
  ]
  const meta = [
    author.filter(info => !!info).join(' - '),
    currentImage?.school || '',
    city.filter(info => !!info).join(' - ')
  ]

  const hasMetaData =
    !!currentImage?.title ||
    author.filter(info => !!info).length > 0 ||
    !!currentImage?.school ||
    city.filter(info => !!info).length > 0

  function isHoliday(day: number) {
    return holidaysBr.map(h => h.day).includes(day)
  }

  const getCalendar = () => {
    const days: Array<string | number> = []
    const weeks: Array<Array<string | number>> = []

    for(let i = 0; i < (totalDays + offset); i++) {
      if(i < offset) {
        days.push('')
      } else {
        days.push((i - offset) + 1)
      }
    }
    
    for(let i = 0; i < days.length; i += 7) {
      let week = days.slice(i, i + 7)
      weeks.push(week)
    }

    // Se houver 6 semanas, mescla a última com a penúltima
    if (weeks.length === 6) {
      const penultima = weeks[4]
      const ultima = weeks[5]

      const mesclada = penultima.map((dia, i) => {
        const extra = ultima[i]
        if (dia === '' && extra !== '') return extra
        if (dia !== '' && extra !== '' && typeof dia === 'number' && typeof extra === 'number') {
          return `${dia}/${extra}`
        }
        return dia
      })

      weeks[4] = mesclada
      weeks.pop()
    }

    return weeks
  }

  return (
    <div className={`calendar-container ${orientation} ${monthName} ${props.featured ? 'featured' : ''}`}>
        <div className="row">
          <ImageContainer month={monthName} />

          <div className="row">

          <div className="meta-data">
            {currentImage?.title && (
              <div className="title">{currentImage.title}</div>
            )}

            {/* Autor e idade */}
            {author.filter(info => !!info).length > 0 && (
              <div className="author">{author.filter(info => !!info).join(' - ')}</div>
            )}

            {/* Escola */}
            {currentImage?.school && (
              <div className="school">{currentImage.school}</div>
            )}

            {/* Cidade e estado */}
            {city.filter(info => !!info).length > 0 && (
              <div className="city">{city.filter(info => !!info).join(' - ')}</div>
            )}
          </div>
        </div>

        <div className="wrapper">

          <div className="calendar-wrapper">
            <div className="calendar-body">
              <h1 className="month">
                {monthTranslations[monthName]} 
                <div className="month-number">
                  {monthNumbers[monthName]} {japaneseMonthSymbol}
                </div>
              </h1>

              <div className="calendar-area">
                <table>
                  <thead>
                    <tr className="tr-br">
                      {
                        weekDaysBr.map((d, i) => (
                          <th key={`${monthName}-${d}-${i}`}>{d.toUpperCase()}</th>
                        ))
                      }
                    </tr>
                    <tr className="tr-jp">
                      {
                        weekDaysJp.map((d, i) => (
                          <th key={`${monthName}-${d}-${i}`}>{d.toUpperCase()}</th>
                        ))
                      }
                    </tr>
                  </thead>

                  <tbody>
                    {getCalendar().map((week, index) => (
                      <tr key={`${monthName}-week-${index}`}>
                        {week.map((day, i) => {
                          const isDual = typeof day === 'string' && day.includes('/')
                          const primaryDay = isDual ? Number(day.split('/')[0]) : Number(day)
                          const secondaryDay = isDual ? Number(day.split('/')[1]) : null
                          const cellClass = `${isHoliday(primaryDay) ? 'text-primary' : ''} ${isDual ? 'dual' : ''}`

                          return (
                            <td key={`${monthName}-week-${index}-day-${i}`} className={cellClass}>
                              {day === '' ? '' : (
                                isDual ? (
                                  <div className="dual-wrapper">
                                    <span className="top">{primaryDay}</span>
                                    <span className="bar"></span>
                                    <span className="bottom">{secondaryDay}</span>
                                  </div>
                                ) : (
                                  day
                                )
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="calendar-footer">

              <div className="br">
                {holidaysBr && holidaysBr.map(({ name, day, block }) => (
                  <span
                    key={`holiday-br-${day}-${name}`}
                    style={{ display: block ? 'block' : 'inline' }}
                  >
                    <span className="day">{day}</span> - {name}
                  </span>
                ))}
              </div>

              <div className="jp">
                {holidaysJp && holidaysJp.map(({ name, day, block }) => (
                <span
                  key={`holiday-jp-${day}-${name}`}
                  style={{ display: block ? 'block' : 'inline' }}
                >
                  <span className="day">{day}</span> - {name}
                </span>
              ))}
              </div>

            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
}
